import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaEdit, FaList, FaPlus, FaSearch, FaStore, FaTrash } from 'react-icons/fa';
import AdminPageHeader from './AdminPageHeader';
import 'bootstrap/dist/css/bootstrap.min.css';
import Navigation from './Navigation';
import { resolveOutletTypeLabel } from './outletTypeOptions';
import { isAdminUser, readStoredAdminUser } from './adminAccessControl';

function Outlets() {
    const [outlets, setOutlets] = useState([]);
    const [filteredOutlets, setFilteredOutlets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const currentUser = readStoredAdminUser();
    const canDeleteOutlets = isAdminUser(currentUser);

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }
        fetchOutlets();
    }, [navigate]);

    const fetchOutlets = async () => {
        setLoading(true);
        const token = localStorage.getItem('sessionToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ limit: 100, start: 0 }),
            });

            const data = await response.json();
            if (data.error === "0") {
                setOutlets(data.results);
                setFilteredOutlets(data.results);
            }
        } catch (err) {
            console.error("Failed to fetch outlets", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearch(value);
        const filtered = outlets.filter(outlet =>
            outlet.outlet_name.toLowerCase().includes(value) ||
            resolveOutletTypeLabel(outlet.outlet_type).toLowerCase().includes(value)
        );
        setFilteredOutlets(filtered);
    };

    const handleEdit = (id) => {
        navigate(`/editoutlet/${id}`);
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const token = localStorage.getItem('sessionToken');
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}deleteoutlet`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ id }),
                    });
                    const data = await response.json();
                    if (data.error === "0") {
                        Swal.fire('Deleted!', 'The outlet has been deleted.', 'success');
                        setOutlets(outlets.filter(outlet => outlet.id !== id));
                        setFilteredOutlets(filteredOutlets.filter(outlet => outlet.id !== id));
                    }
                } catch (err) {
                    console.error(err);
                    Swal.fire('Error!', 'Failed to delete outlet.', 'error');
                }
            }
        });
    };

    const columns = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '80px' },
        {
            name: 'Outlet',
            sortable: true,
            grow: 2,
            cell: row => (
                <div className="table-identity">
                    <span className="table-identity__avatar">
                        {row.outlet_name?.slice(0, 2).toUpperCase() || 'OT'}
                    </span>
                    <span className="table-identity__meta">
                        <strong>{row.outlet_name}</strong>
                        <small>{row.outlet_address}</small>
                    </span>
                </div>
            ),
        },
        { 
            name: 'Contact',
            cell: row => (
                <span className="table-identity__meta">
                    <strong>{row.outlet_phone || '-'}</strong>
                    <small>{row.outlet_email || 'No email'}</small>
                </span>
            ),
            grow: 2
        },
        { 
            name: 'Outlet Type',
            selector: row => resolveOutletTypeLabel(row.outlet_type),
            sortable: true,
            cell: row => (
                <span className="status-chip">
                    {resolveOutletTypeLabel(row.outlet_type)}
                </span>
            ),
        },
        { 
            name: 'Status',
            cell: row => (
                <span className={`badge ${row.status === 1 ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === 1 ? 'Active' : 'Inactive'}
                </span>
            ),
            width: '100px'
        },
        { 
            name: 'Actions',
            cell: row => (
                <div className="table-action-group">
                    <Button variant="outline-warning" size="sm" onClick={() => handleEdit(row.id)}>
                        <FaEdit />
                    </Button>
                    {canDeleteOutlets ? (
                        <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row.id)}>
                            <FaTrash />
                        </Button>
                    ) : null}
                </div>
            ),
            width: '120px',
            right: true
        },
    ];

    return (
        <div>
            <Navigation />
            <Container fluid className="admin-page-shell mt-4 px-4">
                <AdminPageHeader
                    icon={FaStore}
                    eyebrow="Branch operations"
                    title="Outlet Management"
                    description="Atur informasi cabang, status outlet, dan detail kontak yang tampil di sistem."
                    breadcrumbs={[
                        { label: 'Dashboard', to: '/dashboardadmin' },
                        { label: 'Outlet List' },
                    ]}
                    actions={(
                        <Button variant="primary" onClick={() => navigate('/addoutlet')}>
                            <FaPlus /> Add New Outlet
                        </Button>
                    )}
                    meta={(
                        <span className="soft-stat">
                            <FaList /> {filteredOutlets.length} outlets
                        </span>
                    )}
                />
                <div className="section-card table-card">
                    <div className="table-toolbar">
                        <span className="status-chip"><FaList /> {filteredOutlets.length} outlets</span>
                        <div className="control-with-icon search-control">
                            <span className="control-with-icon__icon">
                                <FaSearch />
                            </span>
                            <Form.Control
                                type="text"
                                placeholder="Search outlets..."
                                value={search}
                                onChange={handleSearch}
                            />
                        </div>
                    </div>
                    <div className="card-body pt-0">
                        <AdminDataTable
                            columns={columns}
                            data={filteredOutlets}
                            pagination
                            progressPending={loading}
                            striped
                            emptyTitle="No outlet data found"
                            emptyDescription="Tambahkan outlet baru untuk mulai mengelola cabang dan kontaknya."
                        />
                    </div>
                </div>
            </Container>
        </div>
    );
}

export default Outlets;
